import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './RecentProducts.css';

const RECENT_PRODUCTS_KEY = 'recent-products';
const MAX_RECENT = 8;

function RecentProducts() {
  const [recentProducts, setRecentProducts] = useState([]);
  const [isExpanded, setIsExpanded] = useState(true);

  useEffect(() => {
    loadRecentProducts();

    // Listen for storage changes (when products are added from other tabs/windows)
    const handleStorageChange = (e) => {
      if (e.key === RECENT_PRODUCTS_KEY) {
        loadRecentProducts();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    // Custom event for same-tab updates
    const handleRecentUpdate = () => {
      loadRecentProducts();
    };

    window.addEventListener('recentProductsUpdated', handleRecentUpdate);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('recentProductsUpdated', handleRecentUpdate);
    };
  }, []);

  const loadRecentProducts = () => {
    try {
      const stored = localStorage.getItem(RECENT_PRODUCTS_KEY);
      if (stored) {
        const products = JSON.parse(stored);
        setRecentProducts(products.slice(0, MAX_RECENT));
      }
    } catch (err) {
      console.error('Failed to load recent products:', err);
    }
  };

  const clearRecent = () => {
    localStorage.removeItem(RECENT_PRODUCTS_KEY);
    setRecentProducts([]);
  };

  const removeProduct = (productId, e) => {
    e.preventDefault();
    e.stopPropagation();

    try {
      const stored = localStorage.getItem(RECENT_PRODUCTS_KEY);
      if (stored) {
        const products = JSON.parse(stored);
        const filtered = products.filter(p => p.id !== productId);
        localStorage.setItem(RECENT_PRODUCTS_KEY, JSON.stringify(filtered));
        setRecentProducts(filtered.slice(0, MAX_RECENT));
      }
    } catch (err) {
      console.error('Failed to remove product:', err);
    }
  };

  if (recentProducts.length === 0) {
    return null;
  }

  return (
    <div className={`recent-products-widget ${isExpanded ? 'expanded' : 'collapsed'}`}>
      <div className="recent-header">
        <div className="recent-title">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>Recent Products</span>
          <span className="count">{recentProducts.length}</span>
        </div>
        <div className="recent-actions">
          <button
            className="action-btn"
            onClick={clearRecent}
            title="Clear recent"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
            </svg>
          </button>
          <button
            className="action-btn"
            onClick={() => setIsExpanded(!isExpanded)}
            title={isExpanded ? 'Collapse' : 'Expand'}
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d={isExpanded ? "M4.5 15.75l7.5-7.5 7.5 7.5" : "M19.5 8.25l-7.5 7.5-7.5-7.5"} />
            </svg>
          </button>
        </div>
      </div>

      {isExpanded && (
        <div className="recent-list">
          {recentProducts.map((product) => (
            <Link
              key={product.id}
              to={`/products/${product.id}/edit`}
              className="recent-item"
            >
              <div className="recent-item-content">
                {product.image ? (
                  <img src={product.image} alt={product.name} className="recent-image" />
                ) : (
                  <div className="recent-image-placeholder">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
                    </svg>
                  </div>
                )}
                <div className="recent-info">
                  <div className="recent-name">{product.name}</div>
                  <div className="recent-meta">
                    <span className="recent-sku">{product.sku}</span>
                    <span className="recent-time">{formatTime(product.timestamp)}</span>
                  </div>
                </div>
              </div>
              <button
                className="remove-btn"
                onClick={(e) => removeProduct(product.id, e)}
                title="Remove from recent"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

// Helper function to format timestamp
function formatTime(timestamp) {
  const now = Date.now();
  const diff = now - timestamp;

  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;

  return new Date(timestamp).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
  });
}

// Export utility function to add product to recent list
export function addToRecentProducts(product) {
  try {
    const stored = localStorage.getItem(RECENT_PRODUCTS_KEY);
    let products = stored ? JSON.parse(stored) : [];

    // Remove if already exists
    products = products.filter(p => p.id !== product.id);

    // Add to beginning
    products.unshift({
      id: product.id,
      name: product.name,
      sku: product.sku,
      image: product.images?.[0]?.url || (product.images?.[0]?.isPrimary ? product.images[0].url : null),
      timestamp: Date.now(),
    });

    // Keep only MAX_RECENT items
    products = products.slice(0, MAX_RECENT);

    localStorage.setItem(RECENT_PRODUCTS_KEY, JSON.stringify(products));

    // Dispatch custom event for same-tab updates
    window.dispatchEvent(new Event('recentProductsUpdated'));
  } catch (err) {
    console.error('Failed to add to recent products:', err);
  }
}

export default RecentProducts;
